/**
 * First-use feature tour ("Follow the Spark") config + helpers.
 *
 * The interactive overlay lives in FeatureTour.tsx; this module holds the version gate, the step
 * list, and copy helpers so they can be unit-tested without a DOM. The tour is LIVE: the browse
 * step performs the real move, and every demo step opens the real surface it is pointing at.
 *
 * TWO RULES this file exists to keep honest:
 *  1. A step may only teach a control that is ON THE SCREEN. The old browse step told desktop
 *     readers to use "the left and right arrow keys" while pointing at the card, when the reader
 *     has real arrow buttons in the toolbar; and the old "go deeper" step taught a Quick/Deeper
 *     toggle that only renders on some cards, calling it "Show me Deeper" on every one.
 *  2. A step may only OPEN what this reader can actually open. Since the first door started
 *     letting people in without an account (2026-08-25), the tour runs for signed-out readers
 *     too, and demoing a member feature to them would put the sign-in gate on top of the tour.
 *     Those steps explain and point instead, and say plainly that a free account opens them.
 *
 * House style: NO em dashes or en dashes anywhere in copy. Use commas, periods, or "and".
 */

// Bump to re-show the tour to everyone after a meaningful UI change (like ONBOARD_VERSION).
// v2: the menu moved to the new bottom navigation bar, so the last step now teaches it.
// v3: web-aware copy (click / arrow keys / the View menu) so the desktop tour no longer points
// at the mobile-only bottom bar; the last step is now a device-neutral wrap-up.
// v4: the reader now opens on Key points with a Full / Key points toggle (no more tap/swipe-up to
// expand), so the "go deeper" step teaches the real toggle instead of the removed gesture.
// v5: the browse step's eyebrow and title now adapt to the device too. On a desktop browser it reads
// "Browse / Move between stories" (arrow keys), never "Swipe", so the whole step is consistent with a
// mouse and keyboard instead of contradicting itself (title said Swipe, body said arrow keys).
// v6 (v4 site): the go-deeper step teaches the real Quick / Deeper toggle (the old copy still said
// "Full" and "Key points", labels that no longer exist anywhere on the card), and the wrap-up points
// at the v4 chrome: Brief, Search, Edition and Insights in the top bar, the dock on phones.
// v7: browse points at the real toolbar arrows on a pointer device; the Quick/Deeper step is
// replaced by Deep dive (the feature that actually exists on the card); Verify joins the tour,
// because the receipt is the product's whole argument and no step taught it; and every member
// step is account-aware.
// v8: the browse step points at the arrows that hug the STORY (the pair on its left and right
// edges), not the small pair in the toolbar. Two anchors light at once, because a back and a
// forward arrow on opposite edges are one control and teaching one of them teaches half.
export const TOUR_VERSION = 8;

/** Show the mandatory tour when the reader hasn't completed the current version yet. */
export function shouldShowTour(stored: number | null | undefined): boolean {
  return (stored ?? 0) < TOUR_VERSION;
}

/** Coarse pointer => touch device; drives "Swipe" vs "Use the arrows" copy + gesture mode. */
export function isTouchDevice(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches
  );
}

/** Real app actions the tour drives, supplied by App. */
export interface TourControls {
  nextCard: () => void;
  expand: (open: boolean) => void;
  /** Flip the reader between the Full article and Key points (drives the "Full" toggle demo). */
  showFull: (open: boolean) => void;
  search: (q: string) => void;
  closeSearch: () => void;
  openBrief: () => void;
  closeBrief: () => void;
  openMenu: () => void;
  closeMenu: () => void;
  revealNav: () => void;
  closeNav: () => void;
  /** The Verify drawer: sources, credibility, perspectives. Open to every reader. */
  openVerify: () => void;
  closeVerify: () => void;
  /** The deep dive overlay. Only offered when this card HAS one (see hasDeepDive). */
  openDive: () => void;
  closeDive: () => void;
  /** Whether the card in front of the reader right now carries a deep dive. */
  hasDeepDive: () => boolean;
}

export type TourKind = "welcome" | "spotlight" | "demo";
export type TourHint = "swipeX" | "open" | "tap" | "none";
export type TourGesture = "swipeX" | "open" | null;
export type TourSurface = "search" | "brief" | "menu" | "nav" | "full" | "verify" | "dive" | null;
export type TourPerform = "nextCard" | "expand" | null;
/** A step can require something to be true before it is worth showing at all. */
export type TourNeeds = "deepDive" | null;

export interface TourStep {
  kind: TourKind;
  eyebrow: string;
  title: string;
  /** touch copy */
  body: string;
  /** optional pointer/desktop copy (falls back to the touch `body` / `eyebrow` / `title`).
   *  Use these to drop touch-only words like "Swipe" on a mouse + keyboard. */
  bodyDesktop?: string;
  eyebrowDesktop?: string;
  titleDesktop?: string;
  /** custom primary-button label (defaults to Next / Got it). */
  cta?: string;
  /** data-tour anchor to spotlight (spotlight steps). */
  target?: string;
  /** the anchor to use on a pointer device, when the control differs from the touch one.
   *  A list lights up SEVERAL controls at once, which is the only honest way to teach a pair
   *  that sits on opposite edges of the screen: the story's own back and forward arrows. */
  targetDesktop?: string | string[];
  hint?: TourHint;
  /** a gesture step completes when the reader performs this move. */
  gesture?: TourGesture;
  /** the real action the gesture (or the button) performs, so the demo is live. */
  perform?: TourPerform;
  /** a real surface a demo step opens (after pointing at its button). */
  surface?: TourSurface;
  /** demo "reveal" copy, shown once the surface is opened (phase 2). `body` is the "where" copy. */
  revealBody?: string;
  /** demo phase-1 button label that opens the surface (e.g. "Open search"). */
  openLabel?: string;
  /** the little "you did it" flash on a completed gesture step. */
  nailed?: string;
  /** true when only a signed-in reader can open this. Signed-out readers get `lockedBody` and
   *  the step points instead of opening, so the sign-in gate never lands on top of the tour. */
  requiresAccount?: boolean;
  lockedBody?: string;
  /** drop the step entirely when the condition is not met right now. */
  needs?: TourNeeds;
}

export const TOUR_STEPS: TourStep[] = [
  {
    kind: "welcome",
    eyebrow: "Quick tour",
    title: "Welcome to InSnip",
    body: "Take 30 seconds and we will show you around, so every story works the way you expect.",
    cta: "Show me around",
  },
  {
    kind: "spotlight",
    eyebrow: "Browse",
    title: "Swipe to browse",
    eyebrowDesktop: "Browse",
    titleDesktop: "Move between stories",
    body: "Swipe left for the next story, swipe right to go back.",
    bodyDesktop: "These two arrows sit either side of the story: right for the next one, left to go back. Your arrow keys do the same.",
    target: "card",
    // The arrows that hug the story itself, not the small pair down in the toolbar. Both are
    // lit at once, because the pair is the control and pointing at one of them would teach
    // half of it. `edge-prev` is absent on the first card of the round, and the tour simply
    // lights whichever ones are really there.
    targetDesktop: ["edge-prev", "edge-next"],
    hint: "swipeX",
    gesture: "swipeX",
    perform: "nextCard",
    nailed: "Next story",
  },
  {
    kind: "demo",
    eyebrow: "The receipt",
    title: "Check any story",
    body: "Every card names the publication it came from. Verify opens the rest of the receipt.",
    revealBody: "Every source behind this story, its credibility, and how the Signal score was built.",
    target: "verify-btn",
    surface: "verify",
    hint: "tap",
    openLabel: "Open Verify",
  },
  {
    kind: "demo",
    eyebrow: "Go deeper",
    title: "The whole story, beat by beat",
    body: "Deep dive replays a story: what led here, what changed today, and what it sets up next.",
    revealBody: "That is the deep dive. Close it any time and you are back on the card.",
    lockedBody: "Deep dive replays a story: what led here, what changed today, and what it sets up next. It opens with a free account.",
    target: "deepdive-entry",
    surface: "dive",
    hint: "tap",
    openLabel: "Open deep dive",
    requiresAccount: true,
    needs: "deepDive",
  },
  {
    kind: "demo",
    eyebrow: "Find anything",
    title: "Search any topic",
    body: "Search lives in the top bar. Tap it to look anything up.",
    bodyDesktop: "Search lives in the top bar. Click it, or press / any time, to look anything up.",
    revealBody: "Type a topic and results appear instantly. Here is a live search for climate.",
    lockedBody: "Search every story InSnip has ever run, by name, topic or question. It opens with a free account.",
    target: "search-btn",
    surface: "search",
    hint: "tap",
    openLabel: "Open search",
    requiresAccount: true,
  },
  {
    kind: "demo",
    eyebrow: "The daily Brief",
    title: "Your 60 second Brief",
    body: "The Brief button sits up here in the top bar.",
    revealBody: "The day's biggest stories, distilled into one quick read.",
    lockedBody: "The whole day distilled into a 60 second read. It opens with a free account.",
    target: "brief-btn",
    surface: "brief",
    hint: "tap",
    openLabel: "Open the Brief",
    requiresAccount: true,
  },
  {
    kind: "welcome",
    eyebrow: "You're all set",
    title: "That's InSnip",
    body: "Your dock lives at the bottom: Feed, Stories, Insights and You. Enjoy the signal.",
    bodyDesktop: "Brief, Search, Edition and Insights live in the top bar, and Verify sits under every story. Enjoy the signal.",
    cta: "Start reading",
  },
];

/** The steps this reader actually gets: drop anything the current card cannot demonstrate. */
export function visibleSteps(steps: TourStep[], has: { deepDive: boolean }): TourStep[] {
  return steps.filter((s) => (s.needs === "deepDive" ? has.deepDive : true));
}

/** Pick the copy for the device: pointer devices get the desktop variant when provided.
 *  A locked step (a member feature, no account) always speaks its own line. */
export function stepBody(step: TourStep, touch: boolean, locked = false): string {
  if (locked && step.lockedBody) return step.lockedBody;
  return !touch && step.bodyDesktop ? step.bodyDesktop : step.body;
}

/** Device-aware eyebrow: pointer devices drop touch words like "Swipe" when a variant exists. */
export function stepEyebrow(step: TourStep, touch: boolean): string {
  return !touch && step.eyebrowDesktop ? step.eyebrowDesktop : step.eyebrow;
}

/** Device-aware title: pointer devices drop touch words like "Swipe" when a variant exists. */
export function stepTitle(step: TourStep, touch: boolean): string {
  return !touch && step.titleDesktop ? step.titleDesktop : step.title;
}

/** Every anchor to spotlight for this device, in order. A pointer device uses `targetDesktop`
 *  when set, which may name several controls that belong together. */
export function stepTargets(step: TourStep, touch: boolean): string[] {
  const pick = !touch && step.targetDesktop ? step.targetDesktop : step.target;
  if (!pick) return [];
  return Array.isArray(pick) ? pick.slice() : [pick];
}

/** The first anchor to spotlight for this device. Kept for callers that want one name. */
export function stepTarget(step: TourStep, touch: boolean): string | undefined {
  return stepTargets(step, touch)[0];
}
